/*
 * Purpose: Zod schema for LLM outcome plan JSON validation
 * Last modified: 2026-06-25 | Agent
 * Completeness: 90
 */
import { z } from 'zod';

const plannedStepSchema = z.object({
  id: z.string(),
  blockId: z.string(),
  label: z.string(),
  type: z.enum(['trigger', 'tool', 'action']),
  subtype: z.string(),
  rationale: z.string(),
  blockIntent: z.string().optional(),
  taskOutcome: z.string().optional(),
  categoryRole: z.string().optional(),
  config: z.record(z.unknown()).default({}),
  riskLevel: z.enum(['safe', 'elevated', 'critical']).optional(),
});

const connectorSchema = z.object({
  id: z.string(),
  name: z.string(),
  purpose: z.string(),
  authType: z.string(),
  docsUrl: z.string().optional().default(''),
  setupSteps: z.array(z.string()).default([]),
  status: z
    .enum(['ready', 'needs_credentials', 'needs_scaffold', 'simulated'])
    .default('needs_credentials'),
});

const apiSchema = z.object({
  id: z.string(),
  name: z.string(),
  url: z.string(),
  purpose: z.string(),
  method: z.string().default('POST'),
});

const customBlockSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  type: z.literal('tool'),
  subtype: z.enum(['transform', 'http']),
  defaultConfig: z.record(z.unknown()).default({}),
  rationale: z.string(),
});

export const llmPlanSchema = z.object({
  interpretedGoal: z.string(),
  confidence: z.number().min(0).max(1),
  matchedTemplateId: z.string().optional(),
  useGmailInbound: z.boolean().optional().default(false),
  steps: z.array(plannedStepSchema).min(1),
  connectors: z.array(connectorSchema).default([]),
  apis: z.array(apiSchema).default([]),
  customBlocks: z.array(customBlockSchema).default([]),
  assumptions: z.array(z.string()).default([]),
  gaps: z.array(z.string()).default([]),
  dryRunFixture: z.record(z.unknown()).default({}),
});

export type LlmPlanPayload = z.infer<typeof llmPlanSchema>;
